import { useEffect, useState } from "react";
import { api } from "../api";
import { useT } from "../i18n";
import type { Norms } from "../types";

// Station norms in force (GET /api/norms/current) for labels such as «Коридор 19–21»:
// loaded once per app start, shared by every component, replaced after an admin saves.
export const DEFAULT_NORMS: Norms = { v_lo: 19, v_hi: 21, v_dip: 18, trip_s: 6, f_lo: 49.5, f_hi: 50.5, sharp_kv: 1, prolonged_s: 10 };

let current: Norms | null = null;
let loading: Promise<void> | null = null;
const subscribers = new Set<(n: Norms) => void>();

export function setNorms(n: Norms) {
  current = n;
  subscribers.forEach(f => f(n));
}

export function reloadNorms(): Promise<void> {
  loading = api.get<Norms>("/api/norms/current").then(setNorms).catch(() => { loading = null; });
  return loading;
}

export function useNorms(): Norms {
  const [n, setN] = useState<Norms>(current ?? DEFAULT_NORMS);
  useEffect(() => {
    subscribers.add(setN);
    if (current) setN(current);
    else if (!loading) reloadNorms();
    return () => { subscribers.delete(setN); };
  }, []);
  return n;
}

/** A norm value as people write it: 19 → «19», 18.5 → «18,5» in Russian, «18.5» otherwise. */
export function nf(x: number, lang: string): string {
  const s = String(Math.round(x * 100) / 100);
  return lang === "ru" ? s.replace(".", ",") : s;
}

/** The voltage corridor formatted for t(): «вне {lo}–{hi} кВ». */
export function useBandVars(): { lo: string; hi: string } {
  const n = useNorms();
  const { lang } = useT();
  return { lo: nf(n.v_lo, lang), hi: nf(n.v_hi, lang) };
}
